import React, { useRef } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useLandingTheme } from "../LandingThemeContext";
import { useLandingCopy } from "../LandingLanguageContext";
import { DeferredImage } from "../visuals/DeferredImage";
import { useIsNearViewport } from "../visuals/useIsNearViewport";
import { SectionHeading } from "./shared";

type Testimonial = {
  name: string;
  role: string;
  quote: string;
};

// The file's pixel size gives the avatar's placeholder the same proportions until it loads.
type TestimonialAvatar = { src: string; fileWidth: number; fileHeight: number };

// The students' names, roles and quotes are in landingCopy.ts (testimonials).
const LEFT_TESTIMONIAL_AVATAR: TestimonialAvatar = { src: "/images/landing/testimonial-sam.webp", fileWidth: 648, fileHeight: 786 };
const RIGHT_TESTIMONIAL_AVATAR: TestimonialAvatar = { src: "/images/landing/testimonial-ladedra.webp", fileWidth: 200, fileHeight: 267 };

const TestimonialCard = React.memo(function TestimonialCard({
  testimonial,
  avatar,
  shouldLoadAvatar,
  rotate,
}: {
  testimonial: Testimonial;
  avatar: TestimonialAvatar;
  shouldLoadAvatar: boolean;
  rotate: string;
}) {
  const theme = useLandingTheme();
  const isOriginal = theme.id === "original";
  const radius = isOriginal ? "20px" : theme.shape.cardRadius;
  // Moss's surfaceAlt is a mid moss-green that reads heavy at card size; the near-white surface
  // keeps the cards lighter than the page.
  const quoteBackground = theme.usesOriginalSectionLayouts ? theme.color.surface : theme.color.surfaceAlt;
  // Moss sets the card text 2px smaller than the Original's replica sizes.
  const textSizeOffset = theme.usesOriginalSectionLayouts ? 2 : 0;
  const nameFontSize = { base: `${20 - textSizeOffset}px`, lg: `${24 - textSizeOffset}px` };
  const roleFontSize = { base: `${18 - textSizeOffset}px`, lg: `${20 - textSizeOffset}px` };
  const quoteFontSize = { base: `${16 - textSizeOffset}px`, lg: `${20 - textSizeOffset}px` };
  return (
    <Flex
      transform={`rotate(${rotate})`}
      flexShrink={0}
      borderRadius={radius}
      border={isOriginal ? "none" : theme.card.border}
      boxShadow={isOriginal ? "none" : theme.card.shadow}
      overflow="hidden"
    >
      <DeferredImage
        shouldLoad={shouldLoadAvatar}
        fileWidth={avatar.fileWidth}
        fileHeight={avatar.fileHeight}
        loading="lazy"
        src={avatar.src}
        alt={testimonial.name}
        w={{ base: "112px", md: "145px", lg: "190px" }}
        h={{ base: "auto", md: "194px", lg: "252px" }}
        alignSelf="stretch"
        objectFit="cover"
      />
      <Box
        bg={quoteBackground}
        w={{ base: "223px", md: "269px", lg: "312px" }}
        minH={{ base: "150px", md: "194px", lg: "252px" }}
        p={{ base: "14px", md: "16px", lg: "20px" }}
        fontFamily={theme.font.body}
        lineHeight="1.3"
      >
        <Text fontSize={nameFontSize} fontWeight={600} color={isOriginal ? "#003D16" : theme.color.heading}>
          {testimonial.name}
        </Text>
        <Text fontSize={roleFontSize} fontWeight={500} color={isOriginal ? "#6E7571" : theme.color.muted}>
          {testimonial.role}
        </Text>
        <Text fontSize={quoteFontSize} fontWeight={500} color={isOriginal ? "#0C2100" : theme.color.text} mt="8px">
          {testimonial.quote}
        </Text>
      </Box>
    </Flex>
  );
});

export default function TestimonialsSection() {
  const theme = useLandingTheme();
  const testimonialsCopy = useLandingCopy().testimonials;
  const screenshotRadius = theme.id === "original" ? "20px" : theme.shape.cardRadius;
  // Below the fold: the avatars and screenshots load once the section comes near, not with the first paint.
  const sectionRef = useRef<HTMLDivElement>(null);
  const isSectionNearViewport = useIsNearViewport(sectionRef);
  return (
    <Box ref={sectionRef} as="section" pt={{ base: "154px", md: "188px", lg: "163px" }} overflow="hidden">
      <SectionHeading mb={{ base: "36px", md: "56px" }} px="20px">
        {testimonialsCopy.heading}
      </SectionHeading>

      {/* Wraps naturally: one row on wide desktops, Sam + screenshots then Ladedra on tablets, stacked on phones. */}
      <Flex
        maxW="1340px"
        mx="auto"
        px="20px"
        justify="center"
        align="center"
        wrap="wrap"
        gap={{ base: "18px", md: "32px", lg: "0" }}
        pb="28px"
      >
        <TestimonialCard
          testimonial={testimonialsCopy.leftTestimonial}
          avatar={LEFT_TESTIMONIAL_AVATAR}
          shouldLoadAvatar={isSectionNearViewport}
          rotate="-2deg"
        />

        <Flex direction="column" gap="8px" mx={{ lg: "-10px" }} flexShrink={0}>
          <DeferredImage
            shouldLoad={isSectionNearViewport}
            fileWidth={800}
            fileHeight={254}
            loading="lazy"
            src="/images/landing/testimonial-screenshot-1-w800.webp"
            srcSet="/images/landing/testimonial-screenshot-1-w800.webp 800w, /images/landing/testimonial-screenshot-1.webp 3297w"
            sizes="(min-width: 62em) 371px, (min-width: 48em) 270px, 228px"
            htmlWidth={800}
            htmlHeight={254}
            alt={testimonialsCopy.studentMessageScreenshotAlt}
            w={{ base: "228px", md: "270px", lg: "371px" }}
            borderRadius={screenshotRadius}
            transform="rotate(-1.5deg)"
          />
          <DeferredImage
            shouldLoad={isSectionNearViewport}
            fileWidth={800}
            fileHeight={271}
            loading="lazy"
            src="/images/landing/testimonial-screenshot-2-w800.webp"
            srcSet="/images/landing/testimonial-screenshot-2-w800.webp 800w, /images/landing/testimonial-screenshot-2.webp 2286w"
            sizes="(min-width: 62em) 363px, (min-width: 48em) 269px, 227px"
            htmlWidth={800}
            htmlHeight={271}
            alt={testimonialsCopy.studentMessageScreenshotAlt}
            w={{ base: "227px", md: "269px", lg: "363px" }}
            borderRadius={screenshotRadius}
            transform="rotate(1.5deg)"
          />
        </Flex>

        <TestimonialCard
          testimonial={testimonialsCopy.rightTestimonial}
          avatar={RIGHT_TESTIMONIAL_AVATAR}
          shouldLoadAvatar={isSectionNearViewport}
          rotate="2deg"
        />
      </Flex>
    </Box>
  );
}
